import { activeQuestionAnswers } from './questionRouting';
import type { BuiltinAnswers, BuiltinSourceKey, CustomAnswer, Field, PdfDocumentDefinition, SubmissionPayload } from './types';
import { answerToText, isInputField } from './answers';
import { CONDUCT_VERSION } from './conduct';

/** Built-in questions in sheet order, with the key into BuiltinAnswers and the column header. */
export const BUILTIN_COLUMNS: { key: keyof BuiltinAnswers; header: string }[] = [
  { key: 'firstName', header: 'First name' },
  { key: 'lastName', header: 'Last name' },
  { key: 'ward', header: 'Ward' },
  { key: 'dateOfBirth', header: 'Date of birth' },
  { key: 'telephone', header: 'Telephone number' },
  { key: 'address', header: 'Address' },
  { key: 'city', header: 'City' },
  { key: 'stateOrProvince', header: 'State or province' },
  { key: 'emergencyContact', header: 'Emergency contact (parent or guardian)' },
  { key: 'emergencyPrimaryPhone', header: 'Emergency contact primary phone' },
  { key: 'emergencySecondaryPhone', header: 'Emergency contact secondary phone' },
  { key: 'specialDiet', header: 'Requires special diet?' },
  { key: 'dietExplanation', header: 'Dietary restrictions' },
  { key: 'allergies', header: 'Has allergies?' },
  { key: 'allergyExplanation', header: 'Allergies' },
  { key: 'medications', header: 'Medications' },
  { key: 'selfAdminister', header: 'Can self-administer medication?' },
  { key: 'chronicIllness', header: 'Chronic or recurring illness?' },
  { key: 'chronicIllnessExplanation', header: 'Chronic illness explanation' },
  { key: 'surgery', header: 'Surgery or serious illness in past year?' },
  { key: 'surgeryExplanation', header: 'Surgery or illness explanation' },
  { key: 'otherLimitations', header: 'Other limits, restrictions, or disabilities' },
  { key: 'specialNeeds', header: 'Other accommodations or special needs' },
];

/** Registration answers a form field can show, in the order they're offered: each answer, with the full name before
 * its parts and the full address before its parts. */
export const PDF_BUILTIN_KEYS: BuiltinSourceKey[] = BUILTIN_COLUMNS.flatMap(({ key }): BuiltinSourceKey[] =>
  key === 'firstName' ? ['participant', key] : key === 'address' ? ['fullAddress', key] : key === 'emergencyContact' ? ['emergencyContactName', key] : [key]);
/** The answers each one is made from. */
export const builtinSourceParts = (key: BuiltinSourceKey): (keyof BuiltinAnswers)[] =>
  key === 'participant' ? ['firstName', 'lastName'] : key === 'fullAddress' ? ['address', 'city', 'stateOrProvince'] : key === 'emergencyContactName' ? ['emergencyContact'] : [key];

export const TIMESTAMP_HEADER = 'Timestamp';
export const CONDUCT_HEADER = 'Conduct at Church Activities acknowledged';
export const SIGNED_BY_HEADER = 'Signed by';
export const PDF_HEADER = 'Permission and Medical Release Form (PDF)';
/** Yes for a registration, No for a participant who won't attend; the message is what they shared with the organizer. */
export const ATTENDING_HEADER = 'Attending';
export const MESSAGE_HEADER = 'Message to the organizer';
/** The built-in columns, with Attending and the message right after the participant's name and ward. */
const builtinHeaders = (): string[] => BUILTIN_COLUMNS.flatMap(c => c.key === 'ward' ? [c.header, ATTENDING_HEADER, MESSAGE_HEADER] : [c.header]);

export interface CustomColumn { fieldId: string; row?: string; header: string; /** Set for a form's own question. */ documentId?: string }
/** One column per custom input field, then per question each form asks of its own, headed with the form's
 * name; grids expand to one column per row. */
export function customColumns(fields: Field[], documents: Pick<PdfDocumentDefinition, 'id' | 'title' | 'questions'>[] = []): CustomColumn[] {
  const cols: CustomColumn[] = [];
  const add = (f: Field, prefix = '', documentId?: string) => {
    if (!isInputField(f)) return;
    const label = `${prefix}${f.label}`, owner = documentId ? { documentId } : {};
    if ((f.type === 'choice_grid' || f.type === 'checkbox_grid') && f.grid) {
      for (const r of f.grid.rows) cols.push({ fieldId: f.id, row: r, header: `${label} [${r}]`, ...owner });
    } else {
      cols.push({ fieldId: f.id, header: label, ...owner });
    }
  };
  for (const f of fields) add(f);
  for (const document of documents) for (const f of document.questions) add(f, `${document.title.trim() || 'Additional form'}: `, document.id);
  // Repeated titles (including built-in titles) still need separate spreadsheet cells.
  const used = new Set([TIMESTAMP_HEADER, ...builtinHeaders(), CONDUCT_HEADER, SIGNED_BY_HEADER, PDF_HEADER]);
  return cols.map(col => {
    const original = col.header; let suffix = 2;
    while (used.has(col.header)) col.header = `${original} (${suffix++})`;
    used.add(col.header); return col;
  });
}

export function headerRow(fields: Field[], documents: Pick<PdfDocumentDefinition, 'id' | 'title' | 'questions'>[] = []): string[] {
  return [TIMESTAMP_HEADER, ...builtinHeaders(), ...customColumns(fields, documents).map((c) => c.header), CONDUCT_HEADER, SIGNED_BY_HEADER, PDF_HEADER];
}

function yesNo(v: boolean | null): string {
  return v === null ? '' : v ? 'Yes' : 'No';
}

export function builtinToText(key: keyof BuiltinAnswers, b: BuiltinAnswers): string {
  const v = b[key];
  if (typeof v === 'boolean' || v === null) return yesNo(v);
  return String(v ?? '');
}

/** Build the sheet row for a submission, aligned to `headerRow(fields, documents)`. `formAnswers` holds each
 * form's own answers by document ID. */
export function submissionRow(fields: Field[], payload: SubmissionPayload, timestampIso: string, pdfUrl: string,
  documents: Pick<PdfDocumentDefinition, 'id' | 'title' | 'questions'>[] = [], formAnswers: Record<string, Record<string, CustomAnswer>> = {}): string[] {
  const answers = activeQuestionAnswers(fields, payload.custom);
  const custom = customColumns(fields, documents).map((c) => {
    const f = c.documentId ? documents.find(d => d.id === c.documentId)!.questions.find(x => x.id === c.fieldId)! : fields.find((x) => x.id === c.fieldId)!;
    const a = c.documentId ? formAnswers[c.documentId]?.[c.fieldId] : answers[c.fieldId];
    if (c.row !== undefined) {
      const v = a && typeof a === 'object' && !Array.isArray(a) ? a[c.row] : undefined;
      return Array.isArray(v) ? v.join('; ') : (v ?? '');
    }
    return answerToText(f, a);
  });
  return [
    timestampIso,
    ...BUILTIN_COLUMNS.flatMap((c) => c.key === 'ward' ? [builtinToText(c.key, payload.builtin), 'Yes', ''] : [builtinToText(c.key, payload.builtin)]),
    ...custom,
    payload.conductAcknowledgment === CONDUCT_VERSION ? 'Agreed' : '',
    payload.signedBy === 'participant+parent' ? 'Participant and parent/guardian' : 'Participant',
    pdfUrl,
  ].map(sanitiseCell);
}

/** What a participant who won't attend sends: who they are, and an optional message for the organizer. */
export interface Decline { firstName: string; lastName: string; ward: string; message: string }
/** The sheet row for a participant who won't attend, aligned to `headerRow`: their name and ward, No, and their message. */
export function declineRow(fields: Field[], decline: Decline, timestampIso: string, documents: Pick<PdfDocumentDefinition, 'id' | 'title' | 'questions'>[] = []): string[] {
  const header = (key: keyof BuiltinAnswers) => BUILTIN_COLUMNS.find(c => c.key === key)!.header;
  const cells = new Map([[TIMESTAMP_HEADER, timestampIso], [header('firstName'), decline.firstName], [header('lastName'), decline.lastName], [header('ward'), decline.ward], [ATTENDING_HEADER, 'No'], [MESSAGE_HEADER, decline.message]]);
  return headerRow(fields, documents).map(h => sanitiseCell(cells.get(h) ?? ''));
}

/** Prevent formula injection when a cell is later edited into USER_ENTERED mode. */
export function sanitiseCell(s: string): string {
  return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
}
